/**
 * User Badge Display Hook
 * Provides user-facing badge data for badge display components
 *
 * The profile endpoints return badge IDs only (see #1483): exactly the stored
 * grants the user holds, minus any an admin has hidden. A grant is made by hand
 * or, for a badge linked to an entitlement, automatically while the user holds
 * that entitlement (#2061) — either way it is stored, and nothing is computed
 * at read time. Badge presentation data lives in the
 * badge catalogue, so this hook joins the two: one cheap per-user request for
 * the IDs, plus the shared, cacheable catalogue.
 */

import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useBadgesList } from './useBadgeApi';
import { authenticatedApi } from '../lib/authenticated-api';
import type { BadgeDefinition, UserBadge } from '../lib/types/badge';
import type { UserProfile } from '../lib/types/user';

interface UserBadgeData {
  userBadges: UserBadge[];
  badgeDefinitions: BadgeDefinition[];
  isLoading: boolean;
  error: string | null;
}

interface GetUserResponse {
  user: UserProfile;
}

/**
 * Badge catalogue page size. GET /v1/badges caps `limit` at 500; requesting the
 * maximum keeps the ID → definition join complete without paging.
 */
const BADGE_CATALOGUE_LIMIT = 500;

/**
 * The user's earned badge IDs, from the profile endpoint.
 *
 * Every badge hook reads through here rather than from the auth context.
 * `AuthContext` builds its user from the client-readable `users/{uid}` document
 * and has no `badges` field — nor could it, since the set is read from the
 * `users/{uid}/badges` subcollection server-side. Hooks that read
 * `useAuth().user.badges` therefore saw `undefined` forever.
 *
 * All callers share one react-query entry, so using several of these hooks on a
 * page still costs a single request.
 */
function useUserBadgeIds(userId?: string) {
  const { user: currentUser } = useAuth();
  const targetUserId = userId || currentUser?.uid;

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', targetUserId],
    queryFn: async () => {
      if (!targetUserId) throw new Error('No user ID provided');

      const response = await authenticatedApi.request<GetUserResponse>(
        `/v1/users/${targetUserId}`
      );
      return response.data?.user;
    },
    enabled: !!targetUserId,
  });

  return { userProfile: data, isLoading, error };
}

/**
 * Hook to fetch and combine user badge data for display
 * @param userId - User ID to fetch badges for (optional, defaults to current user)
 */
export function useUserBadgeDisplay(userId?: string): UserBadgeData {
  const {
    userProfile,
    isLoading: isProfileLoading,
    error: userError,
  } = useUserBadgeIds(userId);

  // Fetch the badge catalogue for names and icons. Shared across every user and
  // cached by react-query, so this costs one request regardless of badge count.
  // Request the endpoint maximum so a user's badge is never missed by paging.
  const {
    data: badgeCatalogue,
    isLoading: isCatalogueLoading,
    error: catalogueError,
  } = useBadgesList({ limit: BADGE_CATALOGUE_LIMIT });

  // Memoised so the returned arrays keep a stable identity between renders —
  // fresh arrays every render would defeat downstream memoisation and would
  // loop any consumer that puts them in an effect's dependency list.
  const { userBadges, badgeDefinitions } = useMemo(() => {
    const badgeIds = userProfile?.badges ?? [];

    const definitionsById = new Map(
      (badgeCatalogue?.badges ?? []).map(badge => [badge.badgeId, badge])
    );

    // Only surface badges we can actually render. An ID with no catalogue entry
    // (e.g. a derived badge whose definition has not been seeded) is skipped
    // rather than rendered as a blank tile.
    const resolvedBadgeIds = badgeIds.filter(badgeId =>
      definitionsById.has(badgeId)
    );

    return {
      // earned_at is not carried by the ID-only profile payload. BadgeCard
      // renders without a date when it is absent.
      userBadges: resolvedBadgeIds.map(badgeId => ({
        badgeId,
        granted_by: 'system',
      })) as UserBadge[],
      badgeDefinitions: resolvedBadgeIds.map(
        badgeId => definitionsById.get(badgeId) as BadgeDefinition
      ),
    };
  }, [userProfile?.badges, badgeCatalogue?.badges]);

  // A badge the user holds that the catalogue cannot explain is data loss, and
  // dropping it is otherwise silent. Say so — especially when the catalogue came
  // back truncated, which means the join was incomplete rather than the
  // definition genuinely missing.
  //
  // Only once the catalogue has actually arrived: in the window where the
  // profile has resolved and the catalogue has not, every badge is unresolved
  // and the warning would fire on a state that is merely still loading.
  const unresolvedCount = badgeCatalogue
    ? (userProfile?.badges?.length ?? 0) - userBadges.length
    : 0;
  useEffect(() => {
    if (unresolvedCount > 0) {
      console.warn(
        `⚠️ [useUserBadges] ${unresolvedCount} badge(s) had no catalogue definition and were not displayed.`,
        badgeCatalogue?.hasMore
          ? `The badge catalogue was truncated at ${BADGE_CATALOGUE_LIMIT}; GET /v1/badges needs paginating.`
          : 'Their badge definitions appear to be missing.'
      );
    }
  }, [unresolvedCount, badgeCatalogue?.hasMore]);

  const error =
    (userError as Error | null)?.message ||
    (catalogueError as Error | null)?.message ||
    null;

  return {
    userBadges,
    badgeDefinitions,
    isLoading: isProfileLoading || isCatalogueLoading,
    error,
  };
}

/**
 * Simple hook to check if current user has a specific badge
 * @param badgeId - Badge ID to check
 */
export function useHasBadge(badgeId: string): boolean {
  const { userProfile } = useUserBadgeIds();
  return (userProfile?.badges ?? []).includes(badgeId);
}

/**
 * Hook to get current user's badge count.
 *
 * Counts every badge the user holds, including any whose definition is missing
 * from the catalogue — this answers "how many have I earned", not "how many can
 * be drawn", so it can exceed what `useUserBadgeDisplay` renders.
 */
export function useUserBadgeCount(): number {
  const { userProfile } = useUserBadgeIds();
  return userProfile?.badges?.length ?? 0;
}
